import { useEffect, useRef, useState } from "react";
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from "react-hook-form";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface EnumOption {
  value: string;
  label: string;
}

// shouldBeCustom decides whether the dropdown should sit in free-text mode for
// a given value: any non-empty value that isn't one of the canonical options.
// An empty value is always the placeholder (never custom). Pure so it can be
// unit-tested without a DOM.
export function shouldBeCustom(value: string, options: EnumOption[]): boolean {
  return value !== "" && !options.some((o) => o.value === value);
}

// Shared styling so native <select> elements match the Input component used
// across the forms.
export const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

// EnumSelect renders a dropdown of canonical values plus an "Other…" option
// that reveals a free-text input. Common values are one click, while any
// off-list or legacy value (e.g. a row saved with "dwc" before the list
// existed) stays selectable and editable — the backing fields are free-text,
// so we never want to trap the user in the canonical set.
export function EnumSelect<T extends FieldValues>({
  label,
  name,
  form,
  options,
  required = false,
  placeholder = "— select —",
  help,
  error,
}: {
  label: string;
  name: Path<T>;
  form: UseFormReturn<T>;
  options: EnumOption[];
  required?: boolean;
  placeholder?: string;
  help?: string;
  error?: string;
}) {
  const OTHER = "__other__";
  const value = (form.watch(name) as string | undefined) ?? "";
  const [custom, setCustom] = useState(shouldBeCustom(value, options));

  // Resync custom-mode when the value changes EXTERNALLY — e.g. applyProfile /
  // applySuggestion writing a value via setValue. Without this, a programmatic
  // value that lands a now-canonical option leaves the control stuck in
  // free-text mode (and vice-versa). We react only to an actual change in the
  // watched value, and skip the recompute when the change came from the user's
  // own keystrokes in the free-text box (tracked via `typingRef`) so we never
  // yank them back to a dropdown selection mid-type if what they typed happens
  // to match a canonical option.
  const prevValue = useRef(value);
  const typingRef = useRef(false);
  useEffect(() => {
    if (prevValue.current === value) return;
    prevValue.current = value;
    if (typingRef.current) {
      // User keystroke: don't disturb the free-text box. Reset the flag so the
      // *next* (external) change is reconciled normally.
      typingRef.current = false;
      return;
    }
    setCustom(shouldBeCustom(value, options));
  });

  const customField = form.register(name);
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      <select
        className={selectClass}
        value={custom ? OTHER : value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER) {
            setCustom(true);
            form.setValue(name, "" as PathValue<T, Path<T>>);
          } else {
            setCustom(false);
            form.setValue(name, v as PathValue<T, Path<T>>);
          }
        }}
      >
        <option value="" disabled={required}>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        <option value={OTHER}>Other…</option>
      </select>
      {custom && (
        <Input
          autoFocus
          placeholder="Enter a custom value"
          {...customField}
          onChange={(e) => {
            // Mark this value change as a user keystroke so the resync effect
            // leaves the free-text box alone.
            typingRef.current = true;
            void customField.onChange(e);
          }}
        />
      )}
      {help && !error && (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
